/* eslint-disable react/forbid-component-props */
import { styled } from '@mui/material/styles';
import { QBadge, QTypography } from '@kw/quokka-ui';
import { getMaliciousScoreDisplay, MaliciousScoreVariant } from './maliciousScoreDisplay';

/**
 * Malicious Score column palette.
 *
 * Benign and Suspicious read as a tinted chip so Malicious — the one state that needs attention — is
 * the only solid, loud thing in the row. Every QBadge variant is a solid fill, so the colours are set
 * here instead of by picking one. `accent` colours the score itself, and for Malicious that is the
 * badge's own fill, which keeps the number and the badge reading as a single mark.
 */
export const MALICIOUS_SCORE_TONES: Record<MaliciousScoreVariant, { accent: string; badgeText: string; badgeBackground: string }> = {
  green: { accent: '#37753D', badgeText: '#3e6d43', badgeBackground: '#bcf1cb' },
  yellow: { accent: '#9D632C', badgeText: '#9D632C', badgeBackground: '#FDF6E5' },
  red: { accent: '#DC4C40', badgeText: '#FFFFFF', badgeBackground: '#DC4C40' },
  grey: { accent: '#616161', badgeText: '#616161', badgeBackground: '#EEEEEE' },
};

const MaliciousScoreCellContent = ({
  merlinThreatScore,
  merlinDesignation,
}: {
  merlinThreatScore?: number | null;
  merlinDesignation?: string | null;
}) => {
  const { label, variant, score } = getMaliciousScoreDisplay(merlinThreatScore, merlinDesignation);
  const tone = MALICIOUS_SCORE_TONES[variant];

  return (
    <StyledMaliciousScoreCell>
      {score !== null && (
        <QTypography variant="bodyBold" sx={{ fontSize: '16px', lineHeight: '20px', color: tone.accent }}>
          {score}
        </QTypography>
      )}
      {/* The label stays capitalised in the DOM and is uppercased in CSS, so it still reads as "Benign"
          to screen readers and to anyone searching the page. QBadge overwrites any `& .MuiChip-label`
          passed through `sx`, so the label's padding has to come from `labelPadding` and its type comes
          from the root, which the label inherits. */}
      <QBadge
        label={label}
        labelPadding="0 8px"
        sx={{
          borderRadius: '4px',
          backgroundColor: tone.badgeBackground,
          color: tone.badgeText,
          fontSize: '10px',
          fontWeight: 800,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
        }}
      />
    </StyledMaliciousScoreCell>
  );
};

const StyledMaliciousScoreCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

export default MaliciousScoreCellContent;
